/**
 * Quick Review detail modal for comparing worked vs newly ingested order fields.
 * @module quickReviewModal
 */
import {
  Modal,
  SpinningLoader,
  toast,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { useSelector } from "react-redux";
import { QuickReviewDecision } from "../../../data-model/quickReviewModel";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { InfoIcon } from "../../../svg/info-icon";
import { ToastMessages } from "../../toasts/toastMessages";
import { useQuickReviewDecision } from "./hooks/useQuickReviewDecision";
import { useQuickReviewModalData } from "./hooks/useQuickReviewModalData";
import QuickReviewModalBody from "./quickReviewModalBody";

/** Props for {@link QuickReviewModal}. */
interface QuickReviewModalProps {
  /** Closes the modal and clears redux modal state */
  onClose: () => void;
}

/** Centered spinner shown while the detail endpoint is loading. */
const LoadingState = () => {
  return (
    <div className="flex min-h-20 w-full items-center justify-center">
      <SpinningLoader />{" "}
    </div>
  );
};

/** Fallback UI when the Quick Review detail fetch fails. */
const ErrorState = () => {
  return (
    <div className="flex min-h-20 w-full flex-col items-center justify-center gap-2">
      <InfoIcon stroke="#f50400" width="24px" height="24px" />
      <p className="text-center text-sm font-medium text-primaryGray-3">
        Error fetching quick review data
      </p>
    </div>
  );
};

/**
 * Displays field-level drift for one boarded Quick Review order and exposes
 * reviewer actions (reprocess vs no action needed). Both footer actions apply
 * a decision via the decision endpoint: success closes the modal with a
 * success toast, failure keeps it open and shows an error toast.
 *
 * @param props - {@link QuickReviewModalProps}
 * @returns Oasis modal with loading, error, and detail content states
 */
const QuickReviewModal = ({ onClose }: QuickReviewModalProps) => {
  const { data, loading, error } = useQuickReviewModalData();
  const { metaData } = useSelector((state: RootState) => state.modal);
  const { submitting, handleNoActionNeeded, handleReprocess } =
    useQuickReviewDecision(
      () => {
        toast.success(ToastMessages.QUICK_REVIEW_DECISION_SUCCESS);
        // Ask the board table to re-fetch now that a decision was applied.
        metaData?.onDecisionSuccess?.();
        onClose();
      },
      () => {
        toast.error(ToastMessages.QUICK_REVIEW_DECISION_FAILURE);
      },
    );

  /** Which footer button the reviewer clicked (null until one is clicked). */
  const [clickedDecision, setClickedDecision] =
    useState<QuickReviewDecision | null>(null);

  /** Save footer action: accept the order as-is (`no_action_needed`). */
  const handleSaveClick = () => {
    setClickedDecision(QuickReviewDecision.NO_ACTION_NEEDED);
    return handleNoActionNeeded();
  };

  /** Cancel footer action: send the order back through the pipeline (`reprocess`). */
  const handleCancelClick = () => {
    setClickedDecision(QuickReviewDecision.REPROCESS);
    return handleReprocess();
  };

  return (
    <Modal
      dialogId={ModalId.QUICK_REVIEW_MODAL}
      onSave={handleSaveClick}
      onCancel={handleCancelClick}
      title={"Review"}
      saveButtonText={
        submitting && clickedDecision === QuickReviewDecision.NO_ACTION_NEEDED
          ? "Applying..."
          : "No Action Needed"
      }
      cancelText={
        submitting && clickedDecision === QuickReviewDecision.REPROCESS
          ? "Applying..."
          : "Reprocess Order"
      }
      onClose={onClose}
      disableSave={submitting}
      disableOnCloseFunc
      hideFooter={Boolean(loading || error)}
      maxHeightPercentage={80}
    >
      <div className="quick-review-modal-body">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState />
        ) : (
          <QuickReviewModalBody fields={data?.fields ?? []} />
        )}
      </div>
    </Modal>
  );
};

export default QuickReviewModal;
